import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import Button from "@ui-kit/form/Button/Button.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { Trans } from "next-i18next";
import { useState } from "react";
import PasswordResetForm from "../PasswordResetForm/PasswordResetForm.web";
import type { PasswordResetSharedProps } from "./PasswordReset.props";

export type PasswordResetProps = PasswordResetSharedProps & {
  // Add any web specific props here.
};

export default function PasswordReset(props: PasswordResetProps): JSX.Element {
  const { onLoginClick } = props;
  const { t } = useTranslation("common");
  const [linkSent, setLinkSent] = useState<boolean>();
  const [emailUsed, setEmailUsed] = useState<string>();

  const renderMessage = () => {
    if (linkSent) {
      return (
        <Box>
          <Text mb="lg" fontWeight="bold">
            {t("Your reset link is on the way!")}
          </Text>
          <Text>
            <Trans t={t}>
              If there is a Showpass account for <strong>{emailUsed}</strong> we
              will send you a reset link to create a new password.
            </Trans>
          </Text>
          <Text mt="xs">
            {t(
              "If you don't receive an email within 15 minutes, please check your spam folder."
            )}
          </Text>
        </Box>
      );
    }

    return (
      <Text>
        {t(
          "Forgot your password? No worries! We'll send you a link to reset it, just enter your email below."
        )}
      </Text>
    );
  };

  return (
    <Box w="100%">
      <Text fontSize="40px" fontWeight="bold">
        {!linkSent ? t("Password reset") : t("Email sent")}
      </Text>
      <Box h="element.2xl" />
      {renderMessage()}
      <Box h="element.xl" />
      {!linkSent && (
        <PasswordResetForm
          onSuccess={() => setLinkSent(true)}
          setEmailUsed={setEmailUsed}
        />
      )}
      <Box h="element.2xl" />
      <Flex width="100%" alignItems="center" justifyContent="center">
        {!linkSent ? (
          <Button
            data-testid="password-reset-i-remember-my-password"
            variant="link"
            color="gray.500"
            fontSize="md"
            onClick={onLoginClick}
          >
            {t("I remember my password!")}
          </Button>
        ) : (
          <Button
            data-testid="password-reset-back-to-login"
            width="100%"
            variant="solid"
            onClick={onLoginClick}
          >
            {t("Back to log in")}
          </Button>
        )}
      </Flex>
    </Box>
  );
}
